import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  FiAlertTriangle,
  FiArrowLeft,
  FiCheckCircle,
  FiChevronDown,
  FiLoader,
  FiShield,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import { useI18n } from '../../../i18n';
import {
  createAutoScheduleEngine,
  eachDate,
  totalsBySite,
  type ApplyResult,
  type AutoScheduleEngine,
  type DateRange,
  type DayDemand,
  type PlannedAssignment,
  type ScheduleDraft,
  type UnfilledBlock,
} from '../../../services/autoSchedule';
import { autoScheduleService } from '../../../services/autoScheduleService';
import { formatDateKeyLabel } from '../../../utils/siteScheduling';
import {
  DEFAULT_SHIFT_HOURS,
  MAX_SITE_DAYS,
  MAX_SITES,
  SCHEDULE_PERIODS,
  SHIFT_LENGTH_OPTIONS,
  contractLabel,
  formatHours,
  formatShiftTime,
  openShiftReasons,
  periodLabel,
  periodName,
  periodRange,
  standingLabel,
  standingTone,
  type SchedulePeriod,
} from './schedulePresentation';
import SitePicker, { type ScheduleSiteOption } from './SitePicker';
import { useAutoSchedule } from './useAutoSchedule';
import './AutoScheduleModal.css';

const defaultEngine = createAutoScheduleEngine({ gateway: autoScheduleService });

export interface AutoScheduleModalProps {
  /** Every site that can be scheduled. */
  sites: ScheduleSiteOption[];
  /** The sites ticked when the dialog opens: usually the one on the board. */
  initialSiteIds: string[];
  /** The day the board is showing; the period is drawn around it. */
  anchorDate: string;
  initialPeriod: SchedulePeriod;
  onClose: () => void;
  /** Drafts were written; the board should reload whatever it shows of these sites and dates. */
  onApplied: (siteIds: string[], range: DateRange, result: ApplyResult) => void;
  /** Swappable for tests; defaults to the Supabase-backed engine. */
  engine?: AutoScheduleEngine;
}

/**
 * Drafts a fully staffed roster for one site, several, or all of them, over a
 * day, week or month. The sites are planned together from one pool of guards.
 * Nothing is written until the draft has been reviewed and confirmed, and what
 * is written is a draft: each day still goes through publication, and its rule
 * check, on the board.
 */
export default function AutoScheduleModal({
  sites,
  initialSiteIds,
  anchorDate,
  initialPeriod,
  onClose,
  onApplied,
  engine = defaultEngine,
}: AutoScheduleModalProps) {
  const { t, tn } = useI18n();
  const titleId = useId();
  const dialogRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const [period, setPeriod] = useState<SchedulePeriod>(initialPeriod);
  const [shiftHours, setShiftHours] = useState(DEFAULT_SHIFT_HOURS);
  const [siteIds, setSiteIds] = useState<string[]>(
    () => sites.filter((site) => initialSiteIds.includes(site.id)).map((site) => site.id),
  );
  const range = useMemo(() => periodRange(period, anchorDate), [anchorDate, period]);
  const siteDays = siteIds.length * eachDate(range.startDate, range.endDate).length;
  const tooManySites = siteIds.length > MAX_SITES;
  const tooLarge = tooManySites || siteDays > MAX_SITE_DAYS;

  const flow = useAutoSchedule(engine);
  const { step, draft } = flow;
  const isSaving = step === 'saving';
  const isBusy = step === 'drafting' || isSaving;

  useDialogFocus(dialogRef);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [step]);

  const requestClose = () => {
    if (!isSaving) onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      requestClose();
    }
  };

  const startDraft = () => {
    if (siteIds.length === 0 || tooLarge) return;
    void flow.makeDraft(siteIds, { ...range, preferredShiftMinutes: shiftHours * 60 });
  };

  const handleSave = async () => {
    const saved = await flow.save();
    if (saved && draft) {
      onApplied(draft.siteIds, { startDate: draft.options.startDate, endDate: draft.options.endDate }, saved);
    }
  };

  const shownSiteIds = draft?.siteIds ?? siteIds;
  const subtitleSites = shownSiteIds.length === 1
    ? sites.find((site) => site.id === shownSiteIds[0])?.name ?? ''
    : tn('{count} site', '{count} sites', shownSiteIds.length);
  const hasWork = Boolean(draft && (draft.totals.assignedShifts > 0 || draft.totals.newPosts > 0));
  const stepNumber = step === 'setup' || step === 'drafting' || step === 'error' ? 1 : 2;

  return (
    <div
      className="asch-overlay"
      role="presentation"
      onMouseDown={(event) => { if (event.target === event.currentTarget) requestClose(); }}
    >
      <div
        ref={dialogRef}
        className="asch-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <header className="asch-header">
          <div>
            <p className="asch-eyebrow">{t('Duties board')}</p>
            <h2 id={titleId} className="asch-title">{t('Auto schedule')}</h2>
            <p className="asch-subtitle">{subtitleSites} · {periodLabel(period, range)}</p>
          </div>
          {step !== 'done' && (
            <ol className="asch-steps" aria-label={t('Steps')}>
              <li className={`asch-step${stepNumber === 1 ? ' is-current' : ' is-complete'}`} aria-current={stepNumber === 1 ? 'step' : undefined}>
                <span className="asch-step__number" aria-hidden="true">1</span>
                {t('Choose sites and period')}
              </li>
              <li className={`asch-step${stepNumber === 2 ? ' is-current' : ''}`} aria-current={stepNumber === 2 ? 'step' : undefined}>
                <span className="asch-step__number" aria-hidden="true">2</span>
                {t('Review and confirm')}
              </li>
            </ol>
          )}
          <button type="button" className="asch-close" onClick={requestClose} disabled={isSaving} aria-label={t('Close')}>
            <FiX aria-hidden="true" />
          </button>
        </header>

        <div ref={bodyRef} className="asch-body" aria-busy={isBusy}>
          {step === 'setup' && (
            <div className="asch-setup">
              <div className="asch-setup__row">
                <fieldset className="asch-field">
                  <legend className="asch-label">{t('Period')}</legend>
                  <div className="asch-segmented">
                    {SCHEDULE_PERIODS.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={`asch-segmented__btn${period === option ? ' is-active' : ''}`}
                        aria-pressed={period === option}
                        onClick={() => setPeriod(option)}
                      >
                        {periodName(option, { t, tn })}
                      </button>
                    ))}
                  </div>
                  <p className="asch-hint">{periodLabel(period, range)}</p>
                </fieldset>

                <div className="asch-field">
                  <label className="asch-label" htmlFor="asch-shift-length">{t('Preferred shift length')}</label>
                  <select
                    id="asch-shift-length"
                    className="form__select asch-select"
                    value={shiftHours}
                    onChange={(event) => setShiftHours(Number.parseInt(event.target.value, 10))}
                  >
                    {SHIFT_LENGTH_OPTIONS.map((hours) => (
                      <option key={hours} value={hours}>{t('{hours} hours', { hours })}</option>
                    ))}
                  </select>
                  <p className="asch-hint">
                    {t('Shifts are cut as close to this as the coverage window and the rules allow.')}
                  </p>
                </div>
              </div>

              <fieldset className="asch-field">
                <legend className="asch-label">{t('Sites')}</legend>
                <SitePicker sites={sites} selectedIds={siteIds} onChange={setSiteIds} />
                {siteIds.length > 1 && (
                  <p className="asch-hint">
                    {t('The sites are planned together from one pool of guards, so rest between sites and contracted hours balance across all of them.')}
                  </p>
                )}
                {tooLarge && (
                  <p className="asch-limit" role="status">
                    <FiAlertTriangle aria-hidden="true" />
                    {tooManySites
                      ? t('Choose up to {max} sites at a time.', { max: MAX_SITES })
                      : t('That is {count} site-days. Draft up to {max} at a time: choose fewer sites or a shorter period.', {
                        count: siteDays,
                        max: MAX_SITE_DAYS,
                      })}
                  </p>
                )}
              </fieldset>

              <ul className="asch-rules">
                <li>{t('Drafts blank static posts across each site’s coverage window, one row for each guard the site needs.')}</li>
                <li>{t('Fills open places on draft duties already on the board. Nothing already rostered is moved or removed.')}</li>
                <li>{t('Only gives a shift to a guard who holds the licence, is not on leave or unavailable, and stays within every roster rule. A shift nobody can work lawfully is left open.')}</li>
                <li>{t('Gives guards at least the bottom of their contracted hours where it can.')}</li>
                <li>{t('Days in the past or already published are left alone.')}</li>
              </ul>
            </div>
          )}

          {step === 'drafting' && (
            <div className="asch-state" role="status">
              <FiLoader className="asch-spin" aria-hidden="true" />
              <p className="asch-state__title">{t('Drafting the roster…')}</p>
              <p className="asch-state__detail">{t('Every shift is checked against the roster rules before you see it.')}</p>
            </div>
          )}

          {step === 'error' && (
            <div className="asch-state asch-state--error" role="alert">
              <FiAlertTriangle aria-hidden="true" />
              <p className="asch-state__title">{t('The schedule could not be drafted')}</p>
              <p className="asch-state__detail">{flow.error}</p>
            </div>
          )}

          {(step === 'review' || isSaving) && draft && (
            hasWork
              ? <DraftReview draft={draft} saveError={flow.error} />
              : <NothingToDraft draft={draft} />
          )}

          {step === 'done' && flow.result && draft && (
            <div className="asch-state asch-state--success" role="status">
              <FiCheckCircle aria-hidden="true" />
              <p className="asch-state__title">
                {tn('{count} shift added to the roster', '{count} shifts added to the roster', flow.result.createdAssignments)}
              </p>
              {draft.siteIds.length > 1 && (
                <p className="asch-state__detail">{tn('Across {count} site.', 'Across {count} sites.', draft.siteIds.length)}</p>
              )}
              <p className="asch-state__detail">
                {t('Everything was saved as a draft. Publish each day from the board when you are ready; publishing checks the rules again.')}
              </p>
            </div>
          )}
        </div>

        <footer className="asch-footer">
          {step === 'review' && draft && hasWork && (
            <p className="asch-footer__status">{t('Saved as drafts. Guards see nothing until you publish.')}</p>
          )}
          <div className="asch-footer__actions">
            {(step === 'setup' || step === 'drafting') && (
              <>
                <button type="button" className="btn btn--secondary" onClick={requestClose}>{t('Cancel')}</button>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={startDraft}
                  disabled={step === 'drafting' || siteIds.length === 0 || tooLarge}
                >
                  <FiZap aria-hidden="true" />
                  {step === 'drafting' ? t('Drafting…') : t('Draft schedule')}
                </button>
              </>
            )}

            {step === 'error' && (
              <>
                <button type="button" className="btn btn--secondary" onClick={flow.backToSetup}>
                  <FiArrowLeft aria-hidden="true" />
                  {t('Back')}
                </button>
                <button type="button" className="btn btn--primary" onClick={startDraft}>{t('Try again')}</button>
              </>
            )}

            {(step === 'review' || isSaving) && draft && (
              <>
                <button type="button" className="btn btn--secondary" onClick={flow.backToSetup} disabled={isSaving}>
                  <FiArrowLeft aria-hidden="true" />
                  {t('Back')}
                </button>
                {hasWork ? (
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={() => { void handleSave(); }}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? t('Saving…')
                      : draft.totals.assignedShifts > 0
                        ? tn('Add {count} shift as drafts', 'Add {count} shifts as drafts', draft.totals.assignedShifts)
                        : tn('Add {count} open post', 'Add {count} open posts', draft.totals.newPosts)}
                  </button>
                ) : (
                  <button type="button" className="btn btn--primary" onClick={requestClose}>{t('Close')}</button>
                )}
              </>
            )}

            {step === 'done' && (
              <button type="button" className="btn btn--primary" onClick={requestClose}>{t('Close')}</button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function SkippedDays({ days }: { days: DayDemand[] }) {
  const { tn } = useI18n();
  const past = days.filter((day) => day.status === 'past').length;
  const published = days.filter((day) => day.status === 'published').length;
  const covered = days.filter((day) => day.status === 'covered').length;
  if (past + published + covered === 0) return null;

  return (
    <ul className="asch-skipped">
      {past > 0 && <li>{tn('{count} day is in the past and was left alone.', '{count} days are in the past and were left alone.', past)}</li>}
      {published > 0 && <li>{tn('{count} day is already published. Unlock it on the board to change it.', '{count} days are already published. Unlock them on the board to change them.', published)}</li>}
      {covered > 0 && <li>{tn('{count} day is already fully rostered.', '{count} days are already fully rostered.', covered)}</li>}
    </ul>
  );
}

const siteNamesOf = (draft: ScheduleDraft): Map<string, string> => (
  new Map(draft.context.sites.map((siteContext) => [siteContext.site.id, siteContext.site.name]))
);

function NothingToDraft({ draft }: { draft: ScheduleDraft }) {
  const { t } = useI18n();
  const names = siteNamesOf(draft);
  return (
    <div className="asch-state" role="status">
      <FiCheckCircle aria-hidden="true" />
      <p className="asch-state__title">{t('There is nothing to draft for this period')}</p>
      {draft.siteIds.length === 1 ? (
        <SkippedDays days={draft.days} />
      ) : (
        <ul className="asch-nothing">
          {draft.siteIds.map((siteId) => (
            <li key={siteId}>
              <p className="asch-nothing__site">{names.get(siteId)}</p>
              <SkippedDays days={draft.days.filter((day) => day.siteId === siteId)} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function RuleCheck({ draft }: { draft: ScheduleDraft }) {
  const { t } = useI18n();
  const sites = draft.context.sites;
  const ruleNames = Array.from(new Set(sites.flatMap((siteContext) => (siteContext.rule ? [siteContext.rule.name] : []))));
  const unruled = sites.filter((siteContext) => !siteContext.rule);

  return (
    <>
      {ruleNames.length > 0 && (
        <div className="asch-check asch-check--success">
          <FiShield aria-hidden="true" />
          <div>
            <p className="asch-check__title">{t('Every drafted shift is within the roster rules')}</p>
            <p className="asch-check__detail">
              {t('Checked against {rule}, plus licences, leave, availability and double-booking.', { rule: ruleNames.join(', ') })}
            </p>
          </div>
        </div>
      )}
      {unruled.length > 0 && (
        <div className="asch-check asch-check--warning">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <p className="asch-check__title">
              {sites.length === 1
                ? t('No roster rules are set up for {country}', { country: unruled[0].site.countryCode || t('this site') })
                : t('No roster rules cover {sites}', { sites: unruled.map((siteContext) => siteContext.site.name).join(', ') })}
            </p>
            <p className="asch-check__detail">
              {t('Licences, leave, availability and double-booking were still checked, and the draft keeps to 11 hours rest, 12-hour shifts and 6 days in a row.')}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function DayList({
  days,
  assignments,
  unfilled,
}: {
  days: DayDemand[];
  assignments: PlannedAssignment[];
  unfilled: UnfilledBlock[];
}) {
  const { t, tn } = useI18n();
  const planned = days.filter((day) => day.status === 'planned');
  const [openDays, setOpenDays] = useState<Set<string>>(() => new Set(planned.slice(0, 1).map((day) => day.date)));

  const toggleDay = (date: string) => setOpenDays((current) => {
    const next = new Set(current);
    if (next.has(date)) next.delete(date);
    else next.add(date);
    return next;
  });

  return (
    <>
      <ul className="asch-days">
        {planned.map((day) => {
          const isOpen = openDays.has(day.date);
          const dayShifts = assignments.filter((assignment) => assignment.block.slotDate === day.date);
          const dayOpen = unfilled.filter((open) => open.block.slotDate === day.date);
          return (
            <li key={day.date} className="asch-day">
              <button
                type="button"
                className={`asch-day__toggle${isOpen ? ' is-open' : ''}`}
                aria-expanded={isOpen}
                onClick={() => toggleDay(day.date)}
              >
                <span className="asch-day__date">{formatDateKeyLabel(day.date)}</span>
                <span className="asch-day__summary">
                  {tn('{count} shift', '{count} shifts', dayShifts.length)}
                  {dayOpen.length > 0 && (
                    <span className="asch-day__open">{tn('{count} open', '{count} open', dayOpen.length)}</span>
                  )}
                </span>
                <FiChevronDown aria-hidden="true" />
              </button>
              {isOpen && (
                <ul className="asch-day__shifts">
                  {dayShifts.map((assignment) => (
                    <li key={assignment.block.key} className="asch-shift">
                      <span className="asch-shift__time">{formatShiftTime(assignment.block.startMinute, assignment.block.endMinute)}</span>
                      <span className="asch-shift__guard">{assignment.guardName}</span>
                      <span className="asch-shift__post">
                        {assignment.block.isNewSlot ? t('New static post') : assignment.block.dutyName}
                      </span>
                      <span className="asch-shift__break">
                        {assignment.breakWindow
                          ? t('Break {time}', { time: formatShiftTime(assignment.breakWindow.startMinute, assignment.breakWindow.endMinute) })
                          : t('No break owed')}
                      </span>
                    </li>
                  ))}
                  {dayOpen.map((open) => (
                    <li key={open.block.key} className="asch-shift asch-shift--open">
                      <span className="asch-shift__time">{formatShiftTime(open.block.startMinute, open.block.endMinute)}</span>
                      <span className="asch-shift__guard">{t('Open||open shift')}</span>
                      <span className="asch-shift__post">
                        {open.block.isNewSlot ? t('New static post') : open.block.dutyName}
                      </span>
                      <span className="asch-shift__break" />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
      <SkippedDays days={days} />
    </>
  );
}

function DraftReview({ draft, saveError }: { draft: ScheduleDraft; saveError: string }) {
  const { t, tn } = useI18n();
  const translators = useMemo(() => ({ t, tn }), [t, tn]);
  const { totals } = draft;
  const names = useMemo(() => siteNamesOf(draft), [draft]);
  const isMultiSite = draft.siteIds.length > 1;
  const bySite = useMemo(
    () => totalsBySite(draft.siteIds, draft.days, draft.assignments, draft.unfilled),
    [draft],
  );
  const [openSites, setOpenSites] = useState<Set<string>>(() => new Set(draft.siteIds.slice(0, 1)));

  const toggleSite = (siteId: string) => setOpenSites((current) => {
    const next = new Set(current);
    if (next.has(siteId)) next.delete(siteId);
    else next.add(siteId);
    return next;
  });

  const weeks = Array.from(new Set(draft.weeks.map((week) => week.weekStart)));

  return (
    <div className="asch-review">
      {saveError && (
        <div className="asch-check asch-check--error" role="alert">
          <FiAlertTriangle aria-hidden="true" />
          <div>
            <p className="asch-check__title">{t('Nothing was saved')}</p>
            <p className="asch-check__detail">{saveError}</p>
          </div>
        </div>
      )}

      <RuleCheck draft={draft} />

      <dl className="asch-stats">
        <div className="asch-stat">
          <dt>{t('Shifts assigned')}</dt>
          <dd>{totals.assignedShifts}</dd>
        </div>
        <div className="asch-stat">
          <dt>{t('Hours')}</dt>
          <dd>{formatHours(totals.assignedMinutes)}</dd>
        </div>
        <div className="asch-stat">
          <dt>{t('Guards')}</dt>
          <dd>{totals.guardsUsed}</dd>
        </div>
        <div className={`asch-stat${totals.openShifts > 0 ? ' asch-stat--warning' : ''}`}>
          <dt>{t('Left open')}</dt>
          <dd>{totals.openShifts}</dd>
        </div>
      </dl>

      {isMultiSite && (
        <section className="asch-section" aria-labelledby="asch-sites-title">
          <h3 id="asch-sites-title" className="asch-section__title">{t('By site')}</h3>
          <table className="asch-table">
            <thead>
              <tr>
                <th scope="col">{t('Site')}</th>
                <th scope="col">{t('Shifts')}</th>
                <th scope="col">{t('Hours')}</th>
                <th scope="col">{t('Left open')}</th>
                <th scope="col">{t('Days left alone')}</th>
              </tr>
            </thead>
            <tbody>
              {bySite.map((site) => (
                <tr key={site.siteId}>
                  <th scope="row">{names.get(site.siteId)}</th>
                  <td>{site.assignedShifts}</td>
                  <td>{formatHours(site.assignedMinutes)}</td>
                  <td className={site.openShifts > 0 ? 'asch-table__warning' : undefined}>{site.openShifts}</td>
                  <td>{site.skippedDays}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {draft.unfilled.length > 0 && (
        <section className="asch-section" aria-labelledby="asch-open-title">
          <h3 id="asch-open-title" className="asch-section__title">
            {tn('{count} shift is left open', '{count} shifts are left open', draft.unfilled.length)}
          </h3>
          <p className="asch-section__lead">
            {t('Nobody available could work these without breaking a rule, so they are drafted as open posts. Fill them on the board, or run auto schedule again once more guards are free.')}
          </p>
          <ul className="asch-open">
            {draft.unfilled.map((open) => (
              <li key={open.block.key} className="asch-open__item">
                <span className="asch-open__when">
                  {isMultiSite && `${names.get(open.block.siteId) ?? ''} · `}
                  {formatDateKeyLabel(open.block.slotDate)} · {formatShiftTime(open.block.startMinute, open.block.endMinute)}
                </span>
                <span className="asch-open__why">{openShiftReasons(open, translators)}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {draft.weeks.length > 0 && (
        <section className="asch-section" aria-labelledby="asch-hours-title">
          <h3 id="asch-hours-title" className="asch-section__title">{t('Hours against contract')}</h3>
          {weeks.map((weekStart) => {
            const rows = draft.weeks.filter((week) => week.weekStart === weekStart);
            return (
              <div key={weekStart} className="asch-week">
                <p className="asch-week__title">{t('Week of {date}', { date: formatDateKeyLabel(weekStart) })}</p>
                <table className="asch-table">
                  <thead>
                    <tr>
                      <th scope="col">{t('Guard')}</th>
                      <th scope="col">{t('Contract')}</th>
                      <th scope="col">{t('Drafted')}</th>
                      <th scope="col">{t('Week total')}</th>
                      <th scope="col"><span className="asch-sr-only">{t('Standing')}</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((week) => (
                      <tr key={week.guardId}>
                        <th scope="row">{week.guardName}</th>
                        <td>{contractLabel(week, translators)}</td>
                        <td>{formatHours(week.draftedMinutes)}</td>
                        <td>{formatHours(week.existingMinutes + week.draftedMinutes)}</td>
                        <td>
                          <span className={`asch-chip asch-chip--${standingTone(week)}`}>{standingLabel(week, translators)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.some((week) => !week.weekSettled) && (
                  <p className="asch-hint">{t('Part of this week is outside the period, so its totals can still grow.')}</p>
                )}
              </div>
            );
          })}
        </section>
      )}

      <section className="asch-section" aria-labelledby="asch-days-title">
        <h3 id="asch-days-title" className="asch-section__title">{t('Day by day')}</h3>
        {isMultiSite ? (
          <ul className="asch-site-days">
            {bySite.map((site) => {
              const isOpen = openSites.has(site.siteId);
              return (
                <li key={site.siteId} className="asch-site-days__site">
                  <button
                    type="button"
                    className={`asch-day__toggle asch-site-days__toggle${isOpen ? ' is-open' : ''}`}
                    aria-expanded={isOpen}
                    onClick={() => toggleSite(site.siteId)}
                  >
                    <span className="asch-day__date">{names.get(site.siteId)}</span>
                    <span className="asch-day__summary">
                      {tn('{count} shift', '{count} shifts', site.assignedShifts)}
                      {site.openShifts > 0 && (
                        <span className="asch-day__open">{tn('{count} open', '{count} open', site.openShifts)}</span>
                      )}
                    </span>
                    <FiChevronDown aria-hidden="true" />
                  </button>
                  {isOpen && (
                    <div className="asch-site-days__body">
                      <DayList
                        days={draft.days.filter((day) => day.siteId === site.siteId)}
                        assignments={draft.assignments.filter((assignment) => assignment.block.siteId === site.siteId)}
                        unfilled={draft.unfilled.filter((open) => open.block.siteId === site.siteId)}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <DayList days={draft.days} assignments={draft.assignments} unfilled={draft.unfilled} />
        )}
      </section>
    </div>
  );
}
